<script setup lang="ts">
/*
 * Wrapper for any input — renders a label, the input slot, an optional hint,
 * and an error in error-red. The input slot is given an `id` and
 * `aria-describedby` via inject (set up via `provide`) so consumers don't have
 * to wire those attributes manually.
 */
import { computed, provide, useId } from 'vue';

const props = withDefaults(
  defineProps<{
    label?: string;
    hint?: string;
    error?: string;
    required?: boolean;
    /** Optional explicit id; useful when the input is set via slot props. */
    inputId?: string;
    /** Smaller, muted label — for dense sub-forms (e.g. the specifier rows). */
    subtle?: boolean;
    /**
     * Compact label — the same `text-xs` size as a section heading, but keeps
     * the medium weight + foreground colour of a normal field label. Keeps
     * config-modal field titles from out-sizing their section headers.
     */
    compact?: boolean;
  }>(),
  {},
);

const labelClass = computed(() =>
  props.subtle
    ? 'text-xs font-normal text-app-muted'
    : props.compact
      ? 'text-xs font-medium text-app-fg'
      : 'text-md font-medium text-app-fg',
);

const autoId = useId();
const id = computed(() => props.inputId ?? autoId);
const hintId = computed(() => `${id.value}-hint`);
const errorId = computed(() => `${id.value}-error`);
const describedBy = computed(() =>
  [props.error ? errorId.value : null, props.hint ? hintId.value : null].filter(Boolean).join(' '),
);

provide('formField', {
  inputId: id,
  describedBy,
  invalid: computed(() => Boolean(props.error)),
});
</script>

<template>
  <div class="flex flex-col gap-1.5">
    <label v-if="label" :for="id" :class="labelClass">
      {{ label }}
      <span v-if="required" class="text-red-600" aria-hidden="true">*</span>
    </label>
    <slot :id="id" :described-by="describedBy" :invalid="Boolean(error)" />
    <p v-if="hint && !error" :id="hintId" class="text-xs text-app-muted">{{ hint }}</p>
    <p v-if="error" :id="errorId" class="text-xs text-red-700">{{ error }}</p>
  </div>
</template>
